import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import NurseDateField from "../NurseDateField";
import { normalizeDateInputValue } from "../dateValueUtils";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

export interface Step13FormValues {
    assessor1Name: string;
    assessor1Date: string;
    assessor1Phone: string;
    assessor2Name: string;
    assessor2Date: string;
    assessor2Phone: string;
    assessor3Name: string;
    assessor3Date: string;
    assessor3Phone: string;
    dateOfOriginalPlan: string;
    dateOfLastReview: string;
    reviewerName: string;
    assessmentNotes: string;
}

export const DEFAULT_STEP13: Step13FormValues = {
    assessor1Name: "", assessor1Date: "", assessor1Phone: "",
    assessor2Name: "", assessor2Date: "", assessor2Phone: "",
    assessor3Name: "", assessor3Date: "", assessor3Phone: "",
    dateOfOriginalPlan: "", dateOfLastReview: "", reviewerName: "", assessmentNotes: "",
};

export interface Step13Props {
    sectionData: Partial<Step13FormValues> | null;
    autofillData?: Partial<Step13FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step13FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const Step13Signatures: React.FC<Step13Props> = ({
    sectionData, autofillData, formId, onSave, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const defaultValues = useMemo<Step13FormValues>(() => {
        const mergedValues: Step13FormValues = {
            ...DEFAULT_STEP13,
            ...(autofillData ?? {}),
            ...(sectionData ?? {}),
        };

        return {
            ...mergedValues,
            assessor1Date: normalizeDateInputValue(mergedValues.assessor1Date),
            assessor2Date: normalizeDateInputValue(mergedValues.assessor2Date),
            assessor3Date: normalizeDateInputValue(mergedValues.assessor3Date),
            dateOfOriginalPlan: normalizeDateInputValue(mergedValues.dateOfOriginalPlan),
            dateOfLastReview: normalizeDateInputValue(mergedValues.dateOfLastReview),
        };
    }, [autofillData, sectionData]);
    const { control, handleSubmit, watch, reset } = useForm<Step13FormValues>({ defaultValues });

    useEffect(() => {
        if (!enableEditing) {
            reset(defaultValues);
        }
    }, [defaultValues, enableEditing, reset]);

    const onSubmit = async (data: Step13FormValues) => { await onSave(data); };
    const handleSave = async (data: Step13FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Signature information saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    const assessorBlock = (
        num: 1 | 2 | 3,
        nameKey: keyof Step13FormValues,
        dateKey: keyof Step13FormValues,
        phoneKey: keyof Step13FormValues,
    ) => (
        <div className="col-12 mb-3">
            <p className="font-semibold text-sm mb-2">Assessor {num}</p>
            <div className="grid">
                <div className="col-12 md:col-5">
                    <label className="font-medium text-sm block mb-1">Name</label>
                    <Controller name={nameKey} control={control} render={({ field: f }) => (
                        <InputText {...f} className="w-full" placeholder="Full name" />
                    )} />
                </div>
                <div className="col-6 md:col-4">
                    <label className="font-medium text-sm block mb-1">Date</label>
                    <Controller name={dateKey} control={control} render={({ field: f }) => (
                        <NurseDateField value={f.value as string} onChange={f.onChange} placeholder="MM/DD/YYYY" className="w-full" />
                    )} />
                </div>
                <div className="col-6 md:col-3">
                    <label className="font-medium text-sm block mb-1">Phone</label>
                    <Controller name={phoneKey} control={control} render={({ field: f }) => (
                        <InputText {...f} className="w-full" placeholder="Phone number" />
                    )} />
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="success" outlined rounded loading={loading} visible={enableEditing} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Signatures", "Assessment team and plan dates.", "pi pi-users")}
                            <div className="p-3 grid">
                                <InfoRow label="Assessor 1" value={values.assessor1Name} />
                                <InfoRow label="Assessor 1 Date" value={values.assessor1Date} />
                                <InfoRow label="Assessor 1 Phone" value={values.assessor1Phone} />
                                <InfoRow label="Assessor 2" value={values.assessor2Name} />
                                <InfoRow label="Assessor 2 Date" value={values.assessor2Date} />
                                <InfoRow label="Assessor 2 Phone" value={values.assessor2Phone} />
                                <InfoRow label="Assessor 3" value={values.assessor3Name} />
                                <InfoRow label="Assessor 3 Date" value={values.assessor3Date} />
                                <InfoRow label="Assessor 3 Phone" value={values.assessor3Phone} />
                                <InfoRow label="Date of Original Plan" value={values.dateOfOriginalPlan} />
                                <InfoRow label="Date of Last Review" value={values.dateOfLastReview} />
                                <InfoRow label="Reviewer Name" value={values.reviewerName} />
                                <InfoRow label="Notes" value={values.assessmentNotes} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Assessors */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Assessment Team", "Individuals who completed this assessment.", "pi pi-users")}
                            <div className="p-3 grid">
                                {assessorBlock(1, "assessor1Name", "assessor1Date", "assessor1Phone")}
                                {assessorBlock(2, "assessor2Name", "assessor2Date", "assessor2Phone")}
                                {assessorBlock(3, "assessor3Name", "assessor3Date", "assessor3Phone")}
                            </div>
                        </Card>

                        {/* Care Plan Dates */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Care Plan Record", "Dates for original plan and most recent review.", "pi pi-calendar")}
                            <div className="p-3 grid">
                                <div className="col-12 md:col-4">
                                    <label className="font-medium text-sm block mb-1">Date of Original Plan</label>
                                    <Controller name="dateOfOriginalPlan" control={control} render={({ field: f }) => (
                                        <NurseDateField value={f.value as string} onChange={f.onChange} placeholder="MM/DD/YYYY" className="w-full" />
                                    )} />
                                </div>
                                <div className="col-12 md:col-4">
                                    <label className="font-medium text-sm block mb-1">Date of Last Review</label>
                                    <Controller name="dateOfLastReview" control={control} render={({ field: f }) => (
                                        <NurseDateField value={f.value as string} onChange={f.onChange} placeholder="MM/DD/YYYY" className="w-full" />
                                    )} />
                                </div>
                                <div className="col-12 md:col-4">
                                    <label className="font-medium text-sm block mb-1">Reviewer Name</label>
                                    <Controller name="reviewerName" control={control} render={({ field: f }) => (
                                        <InputText {...f} className="w-full" placeholder="Review completed by" />
                                    )} />
                                </div>
                                <div className="col-12">
                                    <label className="font-medium text-sm block mb-1">Notes / Additional Remarks</label>
                                    <Controller name="assessmentNotes" control={control} render={({ field: f }) => (
                                        <InputTextarea {...f} className="w-full" rows={4} autoResize placeholder="Any additional notes regarding this assessment" />
                                    )} />
                                </div>
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="success" outlined rounded loading={loading} visible={enableEditing} />
                </div>
            </form>
        </>
    );
};

export default Step13Signatures;
